import { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Certificate Engine - YISRAEL LEARNING CENTER
 * 
 * Displays beautifully designed certificates:
 * 1. Grade Advancement Certificates (K-12)
 * 2. Seminary Entrance Scroll (Special Induction)
 * 3. Degree Diplomas (Associate, Bachelor, Master, Doctorate)
 * 
 * Branding Features:
 * - Seven-Branched Menorah Logo
 * - Gold-foil YKBN Doctrine Seal
 * - 364-Day Solar Circuit Border
 */

// Official seal and signature images
const OFFICIAL_SEAL = "https://static.prod-images.emergentagent.com/jobs/22002ca9-24bb-4912-b3b6-4eb4ba6d87ae/images/5ea2270e4415f9b16eb9c3d5a9c6e790c2d8b49b4cb12d7e58da108a0bb70462.png";
const CHIEF_PRIEST_SIGNATURE = "https://static.prod-images.emergentagent.com/jobs/22002ca9-24bb-4912-b3b6-4eb4ba6d87ae/images/b19c66f1ad1e9da859918d3a7bbee781b3726e04c5d31cd64e2522dddbaddfef.png";

function CertificateEngine({ user, config }) {
  const [view, setView] = useState("list"); // list, grade-cert, seminary-scroll, diploma
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedDegree, setSelectedDegree] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [courseCompletions, setCourseCompletions] = useState([]);
  const [seminaryStatus, setSeminaryStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certificateData, setCertificateData] = useState(null);
  const certificateRef = useRef(null);

  useEffect(() => {
    loadCertificates();
    loadSeminaryStatus();
    loadCourseCompletions();
  }, []);

  const loadCertificates = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/my-certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(response.data.issued_certificates || []);
    } catch (err) {
      console.error("Error loading certificates:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSeminaryStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/seminary/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeminaryStatus(response.data);
    } catch (err) {
      console.error("Error loading seminary status:", err);
    }
  };

  const loadCourseCompletions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/completions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseCompletions(response.data.completions || []);
    } catch (err) {
      console.error("Error loading completions:", err);
    }
  };

  const viewGradeCertificate = async (grade) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/grade-advancement/${grade}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificateData(response.data);
      setSelectedGrade(grade);
      setView("grade-cert");
    } catch (err) {
      alert(err.response?.data?.detail || "Error loading certificate");
    } finally {
      setLoading(false);
    }
  };

  const viewSeminaryScroll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/seminary-entrance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificateData(response.data);
      setView("seminary-scroll");
    } catch (err) {
      alert(err.response?.data?.detail || "Error loading seminary scroll");
    } finally {
      setLoading(false);
    }
  };

  const viewDegreeDiploma = async (degreeType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/certificates/degree/${degreeType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificateData(response.data);
      setSelectedDegree(degreeType);
      setView("diploma");
    } catch (err) {
      alert(err.response?.data?.detail || "Error loading diploma");
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async () => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true
      });
      
      const link = document.createElement("a");
      const filename = view === "grade-cert" 
        ? `YLC-Grade-${selectedGrade}-Certificate.png`
        : view === "seminary-scroll"
        ? `YLC-Seminary-Entrance-Scroll.png`
        : `YLC-${selectedDegree}-Diploma.png`;
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (err) {
      // Fallback to print
      printCertificate();
    }
  };

  const printCertificate = () => {
    const printWindow = window.open('', '_blank');
    const content = certificateRef.current?.innerHTML;
    
    if (printWindow && content) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Certificate - Yisrael Learning Center</title>
          <style>
            @page { size: landscape; margin: 0.5in; }
            body {
              font-family: 'Georgia', 'Times New Roman', serif;
              margin: 0;
              padding: 20px;
              background: #fff;
              -webkit-print-color-adjust: exact;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  // Grade levels for K-12
  const gradeLevels = [
    { id: "K", name: "Kindergarten", phase: "Recognition" },
    { id: "1", name: "Grade 1", phase: "Recognition" },
    { id: "2", name: "Grade 2", phase: "Recognition" },
    { id: "3", name: "Grade 3", phase: "Recognition" },
    { id: "4", name: "Grade 4", phase: "Recognition" },
    { id: "5", name: "Grade 5", phase: "Recognition" },
    { id: "6", name: "Grade 6", phase: "Mechanical" },
    { id: "7", name: "Grade 7", phase: "Mechanical" },
    { id: "8", name: "Grade 8", phase: "Mechanical" },
    { id: "9", name: "Grade 9", phase: "Legal Defense" },
    { id: "10", name: "Grade 10", phase: "Legal Defense" },
    { id: "11", name: "Grade 11", phase: "Legal Defense" },
    { id: "12", name: "Grade 12", phase: "Legal Defense" },
  ];

  // ═══════════════════════════════════════════════════════════════════════════════
  // CERTIFICATE LIST VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  if (view === "list") {
    return (
      <div data-testid="certificate-engine">
        <div className="card" style={{ marginBottom: "20px" }}>
          <h2 style={{ color: "#FFD700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "1.5rem" }}>📜</span>
            Certificate Engine
          </h2>
          <p style={{ color: "#aaa", marginBottom: "20px" }}>
            Your achievements and credentials from Yisrael Learning Center
          </p>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
              Loading certificates...
            </div>
          ) : (
            <>
              {/* K-12 Grade Advancement Certificates */}
              <div style={{ marginBottom: "30px" }}>
                <h3 style={{ color: "#90EE90", marginBottom: "15px", borderBottom: "1px solid #333", paddingBottom: "10px" }}>
                  Grade Advancement Certificates (K-12)
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "12px" }}>
                  {gradeLevels.map(grade => {
                    const hasCert = certificates.some(c => c.type === "grade_advancement" && c.grade === grade.id);
                    const completion = courseCompletions.find(c => c.grade_level === grade.id);
                    
                    return (
                      <div
                        key={grade.id}
                        data-testid={`grade-cert-${grade.id}`}
                        onClick={() => hasCert && viewGradeCertificate(grade.id)}
                        style={{
                          background: hasCert ? "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)" : "#1a1a2a",
                          border: hasCert ? "2px solid #90EE90" : "1px solid #333",
                          borderRadius: "12px",
                          padding: "15px",
                          cursor: hasCert ? "pointer" : "default",
                          opacity: hasCert ? 1 : 0.6,
                          transition: "transform 0.2s"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                          <span style={{ fontSize: "1.2rem" }}>{hasCert ? "🏆" : "🔒"}</span>
                          <span style={{ 
                            fontSize: "0.7rem", 
                            padding: "2px 8px", 
                            background: hasCert ? "#90EE90" : "#444", 
                            color: hasCert ? "#000" : "#888",
                            borderRadius: "10px"
                          }}>
                            {grade.phase}
                          </span>
                        </div>
                        <div style={{ color: hasCert ? "#FFD700" : "#888", fontWeight: "bold" }}>
                          {grade.name}
                        </div>
                        <div style={{ color: "#666", fontSize: "0.8rem", marginTop: "5px" }}>
                          {hasCert ? "Tap to view certificate" : "Complete to earn"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Seminary Entrance Scroll */}
              <div style={{ marginBottom: "30px" }}>
                <h3 style={{ color: "#87CEEB", marginBottom: "15px", borderBottom: "1px solid #333", paddingBottom: "10px" }}>
                  Seminary Entrance Scroll
                </h3>
                <div 
                  data-testid="seminary-scroll-card"
                  onClick={() => seminaryStatus?.gateway_unlocked && viewSeminaryScroll()}
                  style={{
                    background: seminaryStatus?.gateway_unlocked 
                      ? "linear-gradient(135deg, #1a2a3a 0%, #0a1a2a 100%)" 
                      : "#1a1a2a",
                    border: seminaryStatus?.gateway_unlocked ? "2px solid #87CEEB" : "1px solid #333",
                    borderRadius: "12px",
                    padding: "25px",
                    cursor: seminaryStatus?.gateway_unlocked ? "pointer" : "default",
                    opacity: seminaryStatus?.gateway_unlocked ? 1 : 0.6,
                    display: "flex",
                    alignItems: "center",
                    gap: "20px"
                  }}
                >
                  <div style={{ fontSize: "3rem" }}>
                    {seminaryStatus?.gateway_unlocked ? "📜" : "🔐"}
                  </div>
                  <div>
                    <div style={{ color: seminaryStatus?.gateway_unlocked ? "#FFD700" : "#888", fontWeight: "bold", fontSize: "1.1rem" }}>
                      Certificate of Induction
                    </div>
                    <div style={{ color: "#aaa", fontSize: "0.9rem", marginTop: "5px" }}>
                      {seminaryStatus?.gateway_unlocked 
                        ? "Your official induction into the YKBN Seminary. Tap to view."
                        : "Complete Grade 12 to unlock the Seminary Gateway"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Degree Diplomas */}
              <div>
                <h3 style={{ color: "#DDA0DD", marginBottom: "15px", borderBottom: "1px solid #333", paddingBottom: "10px" }}>
                  Seminary Degree Diplomas
                </h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: "15px" }}>
                  {[
                    { id: "associate", title: "Associate of National Watchman Studies", abbr: "A.N.W.S.", duration: "2 years" },
                    { id: "bachelor", title: "Bachelor of Hebrew Identity & Restoration", abbr: "B.H.I.R.S.", duration: "4 years" },
                    { id: "master", title: "Master of National Law & Watchman Protocol", abbr: "M.N.L.W.P.", duration: "6 years" },
                    { id: "doctorate", title: "Doctor of National Governance & Doctrine", abbr: "D.N.G.D.", duration: "8+ years", special: true }
                  ].map(degree => {
                    const hasDiploma = certificates.some(c => c.type === "degree_diploma" && c.degree_type === degree.id);
                    
                    return (
                      <div
                        key={degree.id}
                        data-testid={`degree-${degree.id}`}
                        onClick={() => hasDiploma && viewDegreeDiploma(degree.id)}
                        style={{
                          background: hasDiploma 
                            ? "linear-gradient(135deg, #2a1a3a 0%, #1a0a2a 100%)" 
                            : "#1a1a2a",
                          border: hasDiploma 
                            ? "2px solid #DDA0DD" 
                            : degree.special 
                              ? "1px solid #FFD700" 
                              : "1px solid #333",
                          borderRadius: "12px",
                          padding: "20px",
                          cursor: hasDiploma ? "pointer" : "default",
                          opacity: hasDiploma ? 1 : 0.5
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <span style={{ fontSize: "1.5rem" }}>{hasDiploma ? "🎓" : "🔒"}</span>
                          <span style={{ 
                            fontSize: "0.75rem", 
                            padding: "3px 10px",
                            background: degree.special ? "#FFD700" : "#333",
                            color: degree.special ? "#000" : "#888",
                            borderRadius: "10px"
                          }}>
                            {degree.abbr}
                          </span>
                        </div>
                        <div style={{ color: hasDiploma ? "#FFD700" : "#888", fontWeight: "bold", marginTop: "10px", fontSize: "0.95rem" }}>
                          {degree.title}
                        </div>
                        <div style={{ color: "#666", fontSize: "0.8rem", marginTop: "8px" }}>
                          {degree.duration}
                        </div>
                        {degree.special && (
                          <div style={{ 
                            color: "#FFD700", 
                            fontSize: "0.7rem", 
                            marginTop: "8px",
                            fontStyle: "italic"
                          }}>
                            Requires formal application
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // GRADE ADVANCEMENT CERTIFICATE VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  if (view === "grade-cert" && certificateData?.eligible) {
    return (
      <div data-testid="grade-certificate-view">
        <div className="card no-print" style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={() => setView("list")} style={{ background: "#333" }}>
              ← Back to Certificates
            </button>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={downloadCertificate} style={{ background: "#FFD700", color: "#000" }}>
                📥 Download
              </button>
              <button onClick={printCertificate} style={{ background: "#87CEEB", color: "#000" }}>
                🖨️ Print
              </button>
            </div>
          </div>
        </div>

        {/* Certificate */}
        <div 
          ref={certificateRef}
          style={{
            background: "linear-gradient(135deg, #FFF8DC 0%, #F5DEB3 50%, #FFE4B5 100%)",
            border: "12px solid transparent",
            borderImage: "linear-gradient(45deg, #8B4513, #FFD700, #8B4513, #FFD700, #8B4513) 1",
            borderRadius: "0",
            padding: "50px",
            textAlign: "center",
            maxWidth: "850px",
            margin: "0 auto",
            position: "relative",
            boxShadow: "0 10px 40px rgba(0,0,0,0.3)"
          }}
        >
          {/* 364-Day Solar Circuit Border Pattern */}
          <div style={{
            position: "absolute",
            top: "15px",
            left: "15px",
            right: "15px",
            bottom: "15px",
            border: "3px double #8B4513",
            borderRadius: "5px",
            pointerEvents: "none"
          }} />

          {/* Header with Menorah */}
          <div style={{ marginBottom: "30px" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>🕎</div>
            <h1 style={{
              fontFamily: "'Georgia', serif",
              fontSize: "2.2rem",
              color: "#8B4513",
              margin: "0",
              textTransform: "uppercase",
              letterSpacing: "4px"
            }}>
              Certificate of Grade Advancement
            </h1>
            <div style={{
              width: "250px",
              height: "3px",
              background: "linear-gradient(90deg, transparent, #8B4513, transparent)",
              margin: "15px auto"
            }} />
          </div>

          {/* Body */}
          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1.1rem", color: "#444", marginBottom: "15px" }}>
            This is to certify that
          </p>

          <h2 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "2.5rem",
            color: "#000",
            margin: "0 0 15px 0",
            borderBottom: "2px solid #8B4513",
            display: "inline-block",
            padding: "0 40px 10px"
          }}>
            {certificateData.student_name}
          </h2>

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1.1rem", color: "#444", marginBottom: "15px" }}>
            has successfully completed all requirements for
          </p>

          <h3 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1.8rem",
            color: "#8B4513",
            margin: "0 0 10px 0"
          }}>
            {certificateData.grade_name}
          </h3>

          <p style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1rem",
            color: "#666",
            marginBottom: "5px"
          }}>
            {certificateData.phase} Phase • {certificateData.school} School
          </p>

          <p style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1rem",
            color: "#555",
            fontStyle: "italic"
          }}>
            Focus: {certificateData.focus}
          </p>

          {/* Stats Box */}
          <div style={{
            display: "flex",
            justifyContent: "center",
            gap: "40px",
            margin: "25px 0",
            padding: "15px",
            background: "rgba(139, 69, 19, 0.1)",
            borderRadius: "8px"
          }}>
            <div>
              <div style={{ fontSize: "1.4rem", color: "#8B4513", fontWeight: "bold" }}>
                {certificateData.lessons_completed}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#666" }}>Lessons</div>
            </div>
            <div>
              <div style={{ fontSize: "1.4rem", color: "#8B4513", fontWeight: "bold" }}>
                {certificateData.exams_passed}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#666" }}>Exams Passed</div>
            </div>
            <div>
              <div style={{ fontSize: "1.4rem", color: "#8B4513", fontWeight: "bold" }}>
                {certificateData.average_score}%
              </div>
              <div style={{ fontSize: "0.8rem", color: "#666" }}>Average Score</div>
            </div>
          </div>

          {/* Seal and Signature */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginTop: "40px",
            padding: "0 30px"
          }}>
            {/* Date */}
            <div style={{ textAlign: "left" }}>
              <div style={{ borderTop: "2px solid #8B4513", width: "180px", paddingTop: "10px" }}>
                <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>Date of Completion</p>
                <p style={{ margin: 0, color: "#333", fontWeight: "bold", fontSize: "0.95rem" }}>
                  {new Date(certificateData.issue_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </p>
              </div>
            </div>

            {/* Official Seal */}
            <div style={{ textAlign: "center" }}>
              <img 
                src={OFFICIAL_SEAL}
                alt="Official Seal - Yisrael Learning Center"
                style={{ width: "100px", height: "100px" }}
              />
              <p style={{ margin: "5px 0 0 0", fontSize: "0.7rem", color: "#8B4513", fontWeight: "bold" }}>
                YKBN DOCTRINE SEAL
              </p>
            </div>

            {/* Signature */}
            <div style={{ textAlign: "right" }}>
              <img 
                src={CHIEF_PRIEST_SIGNATURE}
                alt="Chief Priest Signature"
                style={{ width: "140px", height: "auto", marginBottom: "5px" }}
              />
              <div style={{ borderTop: "2px solid #8B4513", width: "180px", paddingTop: "5px", marginLeft: "auto" }}>
                <p style={{ margin: 0, color: "#333", fontWeight: "bold", fontSize: "0.9rem" }}>Chief Priest Ykbn Yoonw</p>
              </div>
            </div>
          </div>

          {/* Certificate Number */}
          <div style={{ marginTop: "30px" }}>
            <p style={{ 
              fontFamily: "monospace", 
              fontSize: "0.8rem", 
              color: "#888",
              margin: 0
            }}>
              Certificate No: {certificateData.certificate_number}
            </p>
          </div>

          {/* Institution */}
          <div style={{ marginTop: "20px" }}>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: "1rem", color: "#8B4513", margin: "0" }}>
              Yisrael Learning Center
            </p>
            <p style={{ fontFamily: "'Georgia', serif", fontSize: "0.85rem", color: "#666", margin: "5px 0 0 0" }}>
              Yisrael United Ministries • Est. 2012
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // SEMINARY ENTRANCE SCROLL VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  if (view === "seminary-scroll" && certificateData?.eligible) {
    return (
      <div data-testid="seminary-scroll-view">
        <div className="card no-print" style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={() => setView("list")} style={{ background: "#333" }}>
              ← Back to Certificates
            </button>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={downloadCertificate} style={{ background: "#FFD700", color: "#000" }}>
                📥 Download
              </button>
              <button onClick={printCertificate} style={{ background: "#87CEEB", color: "#000" }}>
                🖨️ Print
              </button>
            </div>
          </div>
        </div>

        {/* Seminary Scroll */}
        <div 
          ref={certificateRef}
          style={{
            background: "linear-gradient(180deg, #F5E6D3 0%, #E8D5C4 50%, #F5E6D3 100%)",
            border: "8px solid #8B4513",
            borderRadius: "20px",
            padding: "60px 50px",
            textAlign: "center",
            maxWidth: "800px",
            margin: "0 auto",
            position: "relative",
            boxShadow: "0 15px 50px rgba(0,0,0,0.4)"
          }}
        >
          {/* Scroll decoration */}
          <div style={{
            position: "absolute",
            top: "-30px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "3rem"
          }}>
            🕎
          </div>

          <h1 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1.8rem",
            color: "#8B4513",
            margin: "0 0 10px 0",
            textTransform: "uppercase",
            letterSpacing: "3px"
          }}>
            Seminary Entrance Scroll
          </h1>

          <h2 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1.3rem",
            color: "#5D4037",
            margin: "0 0 30px 0",
            fontStyle: "italic"
          }}>
            Certificate of Induction
          </h2>

          <div style={{
            width: "200px",
            height: "2px",
            background: "#8B4513",
            margin: "0 auto 30px"
          }} />

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1.1rem", color: "#444", marginBottom: "15px" }}>
            Let it be known that
          </p>

          <h2 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "2.2rem",
            color: "#000",
            margin: "0 0 20px 0",
            borderBottom: "2px solid #8B4513",
            display: "inline-block",
            padding: "0 30px 10px"
          }}>
            {certificateData.student_name}
          </h2>

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1.1rem", color: "#444", marginBottom: "20px", lineHeight: "1.8" }}>
            having successfully completed all requirements of the K-12 Academic Pipeline,<br />
            including the Senior Practicum and Passover Verification Capstone,<br />
            is hereby inducted into the
          </p>

          <h3 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1.6rem",
            color: "#8B4513",
            margin: "0 0 20px 0"
          }}>
            YKBN SEMINARY
          </h3>

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1rem", color: "#555", marginBottom: "30px" }}>
            The Governance Phase of Yisrael Learning Center
          </p>

          {/* Available Tracks */}
          <div style={{
            background: "rgba(139, 69, 19, 0.1)",
            padding: "20px",
            borderRadius: "10px",
            marginBottom: "30px"
          }}>
            <p style={{ color: "#5D4037", fontWeight: "bold", marginBottom: "10px" }}>
              You are now eligible for the following degree programs:
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: "15px", flexWrap: "wrap" }}>
              {certificateData.available_tracks?.map(track => (
                <div key={track.id} style={{
                  background: "#FFF",
                  padding: "10px 15px",
                  borderRadius: "8px",
                  border: "1px solid #D2B48C"
                }}>
                  <div style={{ fontWeight: "bold", color: "#8B4513", fontSize: "0.85rem" }}>{track.name}</div>
                  <div style={{ color: "#888", fontSize: "0.75rem" }}>{track.duration}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Seal and Signature */}
          <div style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "flex-end",
            marginTop: "30px"
          }}>
            <div style={{ textAlign: "center" }}>
              <img 
                src={OFFICIAL_SEAL}
                alt="Official Seal"
                style={{ width: "90px", height: "90px" }}
              />
            </div>
            <div style={{ textAlign: "center" }}>
              <img 
                src={CHIEF_PRIEST_SIGNATURE}
                alt="Signature"
                style={{ width: "130px", height: "auto", marginBottom: "5px" }}
              />
              <div style={{ borderTop: "2px solid #8B4513", width: "150px", paddingTop: "5px" }}>
                <p style={{ margin: 0, color: "#333", fontSize: "0.85rem" }}>Chief Priest Ykbn Yoonw</p>
              </div>
            </div>
          </div>

          <p style={{ 
            fontFamily: "monospace", 
            fontSize: "0.8rem", 
            color: "#888",
            marginTop: "20px"
          }}>
            Scroll No: {certificateData.scroll_number}
          </p>

          <p style={{ 
            fontFamily: "'Georgia', serif", 
            fontSize: "0.9rem", 
            color: "#8B4513",
            marginTop: "15px"
          }}>
            Yisrael United Ministries • Est. 2012
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // DEGREE DIPLOMA VIEW
  // ═══════════════════════════════════════════════════════════════════════════════
  if (view === "diploma" && certificateData?.eligible) {
    return (
      <div data-testid="diploma-view">
        <div className="card no-print" style={{ marginBottom: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <button onClick={() => setView("list")} style={{ background: "#333" }}>
              ← Back to Certificates
            </button>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={downloadCertificate} style={{ background: "#FFD700", color: "#000" }}>
                📥 Download
              </button>
              <button onClick={printCertificate} style={{ background: "#87CEEB", color: "#000" }}>
                🖨️ Print
              </button>
            </div>
          </div>
        </div>

        {/* Diploma */}
        <div 
          ref={certificateRef}
          style={{
            background: "linear-gradient(135deg, #FFFAF0 0%, #FDF5E6 50%, #FAF0E6 100%)",
            border: "15px solid",
            borderImage: "linear-gradient(45deg, #8B4513, #DAA520, #8B4513) 1",
            padding: "60px",
            textAlign: "center",
            maxWidth: "900px",
            margin: "0 auto",
            position: "relative",
            boxShadow: "0 15px 50px rgba(0,0,0,0.3)"
          }}
        >
          {/* Header */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>🕎</div>
            <h1 style={{
              fontFamily: "'Georgia', serif",
              fontSize: "1.4rem",
              color: "#5D4037",
              margin: "0",
              textTransform: "uppercase",
              letterSpacing: "2px"
            }}>
              Yisrael Learning Center
            </h1>
            <p style={{ 
              fontFamily: "'Georgia', serif", 
              fontSize: "0.9rem", 
              color: "#888",
              margin: "5px 0 0 0"
            }}>
              Yisrael United Ministries • Est. 2012
            </p>
          </div>

          <div style={{
            width: "300px",
            height: "3px",
            background: "linear-gradient(90deg, transparent, #8B4513, transparent)",
            margin: "20px auto"
          }} />

          <h2 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "2rem",
            color: "#8B4513",
            margin: "0 0 20px 0",
            textTransform: "uppercase",
            letterSpacing: "3px"
          }}>
            {certificateData.degree_title}
          </h2>

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1.1rem", color: "#444", marginBottom: "15px" }}>
            This is to certify that
          </p>

          <h2 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "2.5rem",
            color: "#000",
            margin: "0 0 20px 0",
            borderBottom: "3px solid #8B4513",
            display: "inline-block",
            padding: "0 50px 10px"
          }}>
            {certificateData.student_name}
          </h2>

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1.1rem", color: "#444", marginBottom: "20px", lineHeight: "1.8" }}>
            has successfully completed all academic requirements<br />
            and is hereby awarded the degree of
          </p>

          <h3 style={{
            fontFamily: "'Georgia', serif",
            fontSize: "1.8rem",
            color: "#8B4513",
            margin: "0 0 10px 0"
          }}>
            {certificateData.degree_abbreviation}
          </h3>

          <p style={{ fontFamily: "'Georgia', serif", fontSize: "1rem", color: "#555" }}>
            Focus: {certificateData.focus}
          </p>

          {certificateData.ordination_eligible && (
            <p style={{ 
              fontFamily: "'Georgia', serif", 
              fontSize: "0.95rem", 
              color: "#8B4513",
              marginTop: "15px",
              fontStyle: "italic"
            }}>
              Eligible for Chief Priest Ordination
            </p>
          )}

          <p style={{ 
            fontFamily: "'Georgia', serif", 
            fontSize: "1rem", 
            color: "#555",
            marginTop: "20px",
            marginBottom: "30px"
          }}>
            Conferred by {certificateData.conferred_by}
          </p>

          {/* Seal and Signatures */}
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginTop: "40px",
            padding: "0 40px"
          }}>
            <div style={{ textAlign: "left" }}>
              <div style={{ borderTop: "2px solid #8B4513", width: "180px", paddingTop: "10px" }}>
                <p style={{ margin: 0, color: "#666", fontSize: "0.9rem" }}>Date Conferred</p>
                <p style={{ margin: 0, color: "#333", fontWeight: "bold" }}>
                  {new Date(certificateData.issue_date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </p>
              </div>
            </div>

            <div style={{ textAlign: "center" }}>
              <img 
                src={OFFICIAL_SEAL}
                alt="Official Seal"
                style={{ width: "110px", height: "110px" }}
              />
              <p style={{ margin: "5px 0 0 0", fontSize: "0.7rem", color: "#8B4513", fontWeight: "bold" }}>
                YKBN DOCTRINE SEAL
              </p>
            </div>

            <div style={{ textAlign: "right" }}>
              <img 
                src={CHIEF_PRIEST_SIGNATURE}
                alt="Signature"
                style={{ width: "150px", height: "auto", marginBottom: "5px" }}
              />
              <div style={{ borderTop: "2px solid #8B4513", width: "180px", paddingTop: "5px", marginLeft: "auto" }}>
                <p style={{ margin: 0, color: "#333", fontWeight: "bold", fontSize: "0.9rem" }}>Chief Priest Ykbn Yoonw</p>
              </div>
            </div>
          </div>

          <p style={{ 
            fontFamily: "monospace", 
            fontSize: "0.8rem", 
            color: "#888",
            marginTop: "30px"
          }}>
            Diploma No: {certificateData.diploma_number}
          </p>
        </div>
      </div>
    );
  }

  // Default: Not eligible view
  return (
    <div className="card">
      <button onClick={() => setView("list")} style={{ background: "#333", marginBottom: "20px" }}>
        ← Back to Certificates
      </button>
      <div style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "4rem", marginBottom: "20px" }}>📚</div>
        <h3 style={{ color: "#FFD700" }}>Certificate Not Available</h3>
        <p style={{ color: "#aaa" }}>
          {certificateData?.message || "Complete the required courses to earn this certificate."}
        </p>
      </div>
    </div>
  );
}

export default CertificateEngine;
